import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from './bearerAxios';
import { getContractApiErrorMessage } from './contractApiErrorMessage';
import { usePartnerApiErrorStore } from '../store/partnerApiErrorStore';

export const SKIP_PARTNER_ERROR_POPUP_HEADER = 'X-Skip-Partner-Error-Popup';

/** Pass on axios calls that handle errors inline (e.g. expected 404, progress modal). */
export const skipPartnerErrorPopupConfig: AxiosRequestConfig = {
    headers: { [SKIP_PARTNER_ERROR_POPUP_HEADER]: '1' },
};

const PARTNER_URL_MARKERS = [
    'apionebuttoncontract',
    'apionebuttonopportunity',
    'apionebuttondesign',
    'apionebuttonmaster',
    '/api/contract/',
    '/api/sunlight',
    '/api/opportunity',
    'getquotes',
    'createepcpricing',
    'fetch_finance_partner',
    'financepartner',
    'lightreach',
    'sunnova',
    'goodleap',
    'sunlight',
];

function isPartnerApiUrl(url: string | undefined): boolean {
    if (!url?.trim()) return false;
    const lower = url.toLowerCase();
    return PARTNER_URL_MARKERS.some((marker) => lower.includes(marker));
}

function shouldSkipPopup(config: AxiosRequestConfig | undefined): boolean {
    const headers = config?.headers as Record<string, string | undefined> | undefined;
    if (!headers) return false;
    const value = headers[SKIP_PARTNER_ERROR_POPUP_HEADER] ?? headers[SKIP_PARTNER_ERROR_POPUP_HEADER.toLowerCase()];
    return value === '1' || value === 'true';
}

function partnerErrorTitle(url: string | undefined, status?: number): string {
    const statusSuffix = status ? ` (${status})` : '';
    if (!url) return `Partner request failed${statusSuffix}`;
    const lower = url.toLowerCase();
    if (lower.includes('sunlight')) return `Sunlight error${statusSuffix}`;
    if (lower.includes('lightreach')) return `LightReach error${statusSuffix}`;
    if (lower.includes('goodleap')) return `GoodLeap error${statusSuffix}`;
    if (lower.includes('sunnova')) return `Sunnova error${statusSuffix}`;
    if (lower.includes('igs')) return `IGS error${statusSuffix}`;
    if (lower.includes('/api/contract/')) return `Finance partner error${statusSuffix}`;
    return `Partner request failed${statusSuffix}`;
}

let interceptorRegistered = false;

export function registerPartnerApiErrorInterceptor(): void {
    if (interceptorRegistered) return;
    interceptorRegistered = true;

    axios.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
            const config = error.config;
            const url = config?.url ?? error.request?.responseURL;
            const status = error.response?.status;

            const isClientError = status != null && status >= 400 && status < 500;

            if (
                isPartnerApiUrl(typeof url === 'string' ? url : undefined) &&
                !isClientError &&
                !shouldSkipPopup(config) &&
                !usePartnerApiErrorStore.getState().isPartnerErrorPopupSuppressed()
            ) {
                const message = getContractApiErrorMessage(
                    error,
                    status === 404
                        ? 'The requested partner resource was not found.'
                        : 'A partner service request failed. Please try again.',
                );
                usePartnerApiErrorStore.getState().showPartnerApiErrorPopup({
                    title: partnerErrorTitle(typeof url === 'string' ? url : undefined, status),
                    message,
                    statusCode: status,
                });
            }

            return Promise.reject(error);
        },
    );
}

/** Manually surface a partner error in the global popup (non-axios flows). */
export function showPartnerApiErrorFromUnknown(
    error: unknown,
    options?: { title?: string; fallback?: string },
): void {
    if (usePartnerApiErrorStore.getState().isPartnerErrorPopupSuppressed()) return;
    const message = getContractApiErrorMessage(error, options?.fallback ?? 'An error occurred.');
    usePartnerApiErrorStore.getState().showPartnerApiErrorPopup({
        title: options?.title ?? 'Partner request failed',
        message,
    });
}
